import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "components/modal";
import { Button } from "components/button/button.tsx";
import { useSession, useUser } from '@clerk/clerk-react';
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Textarea, Spinner, Avatar } from "@heroui/react";
import { Kaylee } from "assets/icons";
import { toast } from "react-toastify";

interface ChatModalProps {
    isOpen: boolean;
    onOpenChange: () => void;
}

interface Message {
    sender: 'user' | 'kaylee';
    content: string;
}

const KayleeTenantModal: React.FC<ChatModalProps> = ({ isOpen, onOpenChange }) => {
    const { session } = useSession();
    const { user } = useUser();
    const [message, setMessage] = useState('');
    const [response, setResponse] = useState('');
    const [chatLog, setChatLog] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [typingMessage, setTypingMessage] = useState<Message | null>(null);
    const [feedbackRequested, setFeedbackRequested] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [explanation, setExplanation] = useState('');
    const [satisfaction, setSatisfaction] = useState<string | null>(null);
    const [badAnswerType, setBadAnswerType] = useState<string | null>(null);
    const [chatId, setChatId] = useState<string | null>(null);
    const [promptNum, setPromptNum] = useState(1);  // Start from 1 for the first question
    const [question, setQuestion] = useState('');
    const [disabled, setDisabled] = useState<boolean>(false);

    const generateUniqueId = () => {
        return Math.random().toString(36).substr(2, 9);
    }

    useEffect(() => {
        setChatId(generateUniqueId());
    }, []);


    useEffect(() => {
        if (isOpen) {
            setDisabled(true);
            const greetingContent = getGreetingMessage(user?.firstName || "");
            simulateTyping(greetingContent, "kaylee", () => {
                setDisabled(false);
            });
        }
    }, [isOpen, user]);

    const clearCache = () => {
        setChatLog([]);
        setQuestion('');
        setFeedbackRequested(false);
        setPromptNum(1) // reset prompt number to 1
        setChatId(generateUniqueId());
        setExplanation('');
        setFeedbackText('');
        setDisabled(false);
        setIsLoading(false);
        setMessage(''); // Clear the initial question text
        setSatisfaction(null); // Clear satisfaction state
        onOpenChange();
    }

    const handleOpenChange = () => {
        sendMessage('exit');
    }


    const simulateTyping = (fullMessage: string, sender: 'user' | 'kaylee', callback?: () => void) => {
        setTypingMessage({ sender, content: '' });
        let idx = 0;

        const typeNextLetter = () => {
            if (idx < fullMessage.length) {
                setTypingMessage({ sender, content: fullMessage.substring(0, idx + 1) });
                idx++;
                setTimeout(typeNextLetter, 35); // increase or decrease speed
            } else {
                setTypingMessage(null);
                setChatLog(prevLog => [...prevLog, { sender, content: fullMessage }]);
                if (callback) callback();
            }
        };

        typeNextLetter();
    };


    const getGreetingMessage = (name: string) => {
        const hour = new Date().getHours();
        let greeting;

        if (hour < 12) {
            greeting = "Good morning";
        } else if (hour < 18) {
            greeting = "Good afternoon";
        } else {
            greeting = "Good evening";
        }

        return `${greeting} ${name}, please paste any question from the tenant here and I will suggest the right answer? Afterwards, give me feedback so that I can keep learning.`;
    }

    const sendMessage = async (customQuestion?: string) => {
        // Start fetching data immediately
        const fetchData = async () => {
            setIsLoading(true);
            setDisabled(true);
            const finalQuestion = (customQuestion && customQuestion != '' && customQuestion) || message;
            setMessage(finalQuestion);

            setChatLog(prevLog => [...prevLog, { sender: 'user', content: message }]);

            if (finalQuestion == 'exit') {
                clearCache();
                return; // Exit early, so we don't process further
            }

            setQuestion(message);

            try {
                const response = await fetchWithAuth(`${API_URL}/ai/chat/ask`, {
                    method: 'POST',
                    headers: {
                        'Authorization': (await session?.getToken()) as string,
                        'x-session-id': (await session?.id) as string,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ question: message }),
                }, session);

                const data = await response.json();
                setResponse(data.answer);
                setPromptNum(prevPromptNum => prevPromptNum + 1);

                return data.answer;
            } catch (error) {
                console.error("Error sending message:", error);
                simulateTyping("Something went wrong. Close the chat and try again.", 'kaylee');
            } finally {
                setIsLoading(false);
                setDisabled(false);
            }
        };

        const fetchPromise = fetchData();

        // Wait for 2 seconds, then simulate Kaylee's typing
        setTimeout(() => {
            simulateTyping("I'm formulating an answer, please hold on for a few seconds.", 'kaylee', async () => {
                const fetchedReply = await fetchPromise;
                simulateTyping(fetchedReply as string, "kaylee");
                setFeedbackRequested(true);
                setIsLoading(false);
                setDisabled(false);
                setMessage('');
            });
        }, 2000);
    };

    const submitFeedback = async (satisfying: any) => {
        setDisabled(true);
        const finalSatisfaction = satisfying || satisfaction;
        try {
            await fetchWithAuth(`${API_URL}/ai/chat/feedback`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        question: question,
                        answer: response,
                        satisfying: finalSatisfaction == 'yes',
                        alternativeAnswer: feedbackText,
                        badAnswerType: finalSatisfaction == 'no' ? badAnswerType : null,
                        chatId: chatId,
                        promptNum: promptNum - 1,
                        user: user?.fullName || 'unknown',
                        explanation: explanation
                    }),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }, session);

            setFeedbackRequested(false);
            setExplanation('');
            setQuestion('');
            setDisabled(false);
            setFeedbackText('');
            toast.success('Feedback sent!');
            setMessage(''); // Clear the initial question text
            setSatisfaction(null); // Clear satisfaction state
        } catch (err) {
            toast.error("Something went wrong " + err);
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    }

    return (
        <Modal size="3xl" isOpen={isOpen} onOpenChange={() => {
            handleOpenChange();
        }}>
            <ModalContent>
                <ModalHeader>
                    Kaylee beta (Helpdesk assistant mode)
                </ModalHeader>
                <ModalBody style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    {!feedbackRequested ? (<>
                        <div className="chatLog">
                            {chatLog.map((msg, idx) => (
                                <div key={idx} className={`flex gap-2 mb-4 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    {msg.sender === "kaylee" && <Button radius="full" isIconOnly size="sm" className="self-start"><Kaylee /></Button>}
                                    <p className={`text-align-${msg.sender === 'user' ? 'right' : 'left'}`}>{msg.content}</p>
                                    {msg.sender === "user" && <Avatar src={user?.imageUrl || "defaultUserImageUrl"} size="sm" className="self-start" />}
                                </div>
                            ))}
                            {typingMessage && (
                                <div className={`flex gap-2 mb-4 ${typingMessage.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    {typingMessage.sender === 'kaylee' && <Button radius="full" isIconOnly size="sm" className="self-start"><Kaylee /></Button>}
                                    <p className={`text-align-${typingMessage.sender === 'user' ? 'right' : 'left'}`}>{typingMessage.content}</p>
                                    {typingMessage.sender === 'user' && <Avatar src={user?.imageUrl || "defaultUserImageUrl"} size="sm" className="self-start" />}
                                </div>
                            )}
                        </div>
                        {isLoading && <Spinner label="Browsing through the Yally encyclopedia..." color="warning" />}
                        <Textarea
                            type="text"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter the tenant's question here"
                            disabled={disabled}
                        />
                    </>) : (
                        <div className="chatLog">
                            <>
                                {/* Displaying last question and answer in the modal */}
                                <div className="mb-4">
                                    <div className="text-olijfgroen mb-2 font-semibold">
                                        <i className="fa-solid fa-user text-lg"></i> You:
                                        <span className="ml-1 font-normal">{[...chatLog].reverse().find(msg => msg.sender === 'user')?.content}</span>
                                    </div>
                                    <div className="text-dunkergroen mb-4 font-semibold">
                                        <i className="fa-solid fa-robot text-lg"></i> AI:
                                        <span className="ml-1 font-normal">{[...chatLog].reverse().find(msg => msg.sender === 'kaylee')?.content}</span>
                                    </div>
                                </div>

                                <p className="mb-4 font-bold text-grafiet">Was the answer satisfying?</p>
                                <div className="flex space-x-4 mb-4">
                                    <Button
                                        color="success"
                                        variant={satisfaction === 'yes' ? 'bordered' : 'flat'}
                                        onClick={() => submitFeedback('yes')}
                                    >
                                        Yes
                                    </Button>
                                    <Button
                                        color="danger"
                                        variant={satisfaction === 'no' ? 'bordered' : 'flat'}
                                        onClick={() => setSatisfaction('no')}
                                    >
                                        No
                                    </Button>
                                </div>

                                {satisfaction === 'no' && (
                                    <>
                                        <div className="mt-4">
                                            <p className="mb-4 font-bold text-grafiet">Was the answer bad in general or in the context of chat history?</p>
                                            <div className="flex space-x-4 mb-4">
                                                <Button
                                                    color="default"
                                                    variant={badAnswerType === 'general' ? 'bordered' : 'flat'}
                                                    onClick={() => setBadAnswerType('general')}
                                                >
                                                    In general
                                                </Button>
                                                <Button
                                                    color="default"
                                                    variant={badAnswerType === 'context' ? 'bordered' : 'flat'}
                                                    onClick={() => setBadAnswerType('context')}
                                                >
                                                    In context
                                                </Button>

                                            </div>
                                        </div>
                                        <Textarea
                                            label="Explain for the developers why the AI should not answer in this way:"
                                            rows={2}
                                            placeholder="Explain here..."
                                            value={explanation}
                                            onChange={e => setExplanation(e.target.value)}
                                        />
                                        <Textarea
                                            label="Create a new answer"
                                            rows={4}
                                            placeholder="Type the literal answer as the AI should say it here..."
                                            value={feedbackText}
                                            onChange={e => setFeedbackText(e.target.value)}
                                        />
                                    </>
                                )}
                            </>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    {!feedbackRequested ? (
                        <Button onClick={() => sendMessage()} disabled={disabled}>Send</Button>
                    ) : satisfaction === 'no' ? (
                        <Button disabled={disabled} onClick={() => submitFeedback(satisfaction)}>
                            Send feedback
                        </Button>
                    ) : ""}
                </ModalFooter>
            </ModalContent>

        </Modal >
    );
}

export default KayleeTenantModal;
